import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@pilot-hub/ui'
import { checklistenApi } from '@pilot-hub/api-client'
import { ChecklistZeile } from './ChecklistZeile'
import { useAlleOk } from './AlleOk'
import { ChecklistenEingabe } from './ChecklistenEingabe'

// Dialog für eine einzelne Flug-Checkliste (z. B. Vorflugkontrolle nachtragen): Punkte abhaken, abschließen.
// Speichert jeden Punkt sofort (hintereinander, nie parallel) und zeigt den Haken sofort an.

interface Punkt { id: number; text: string; typ: 'CHECK' | 'TEXT' | 'NUMBER' | 'PHOTO'; pflicht: boolean; hinweis: string | null; wert: string | null; sortierung: number }
interface Instanz { id: number; name: string; status: string; items: Punkt[] }

export function ChecklisteDialog({ einsatzId, instanzId, titel, hinweis, onClose }: {
  einsatzId: number; instanzId: number; titel: string; hinweis?: string; onClose: (erledigt: boolean) => void
}) {
  const [instanz, setInstanz] = useState<Instanz | null>(null)
  const { frage: frageAlleOk, dialog: alleOkDialog } = useAlleOk()
  const [fehler, setFehler] = useState<string | null>(null)
  const [speichert, setSpeichert] = useState(false)
  const kette = useRef<Promise<unknown>>(Promise.resolve())

  const laden = useCallback(async () => {
    try {
      const r = await checklistenApi.getEinsatzChecklisten(einsatzId)
      const i = ((r.data as any[]) ?? []).find(c => c.id === instanzId)
      if (i) setInstanz(i as Instanz); else setFehler('Checkliste nicht gefunden')
    } catch (e: any) { setFehler(e?.response?.data?.detail || 'Checkliste konnte nicht geladen werden') }
  }, [einsatzId, instanzId])
  useEffect(() => { void laden() }, [laden])

  function setzen(itemId: number, wert: string) {
    setInstanz(i => i && { ...i, items: i.items.map(p => p.id === itemId ? { ...p, wert } : p) })
    kette.current = kette.current.then(() => checklistenApi.updateInstanzItem(instanzId, itemId, { wert }))
      .catch((e: any) => { setFehler(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); void laden() })
  }
  function alleSetzen() {
    instanz?.items.filter(p => p.typ === 'CHECK' && p.pflicht && !p.wert).forEach(p => setzen(p.id, 'true'))
  }
  const offen = instanz ? instanz.items.filter(p => p.pflicht && !p.wert).length : 0

  async function abschliessen() {
    setSpeichert(true); setFehler(null)
    try {
      await kette.current
      await checklistenApi.updateInstanz(instanzId, { status: 'ABGESCHLOSSEN' })
      onClose(true)
    } catch (e: any) { setFehler(e?.response?.data?.detail || 'Abschließen fehlgeschlagen') } finally { setSpeichert(false) }
  }

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" data-testid="checkliste-dialog">
      {alleOkDialog}
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-dark-border bg-dark-bg">
        <div className="border-b border-dark-border bg-dark-bg-secondary p-4">
          <h2 className="text-lg font-semibold text-dark-text">{titel}</h2>
          {hinweis && <p className="text-sm text-dark-text-muted">{hinweis}</p>}
        </div>
        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{fehler}</div>}
          {!instanz && !fehler && <p className="text-dark-text-muted">Lädt …</p>}
          {instanz?.items.map(item => (
            <ChecklistZeile key={item.id} typ={item.typ} className="rounded-xl border border-dark-border bg-dark-bg-tertiary/50 p-3">
              <div className="flex items-center gap-3">
                {item.typ === 'CHECK' && (
                  <input type="checkbox" checked={item.wert === 'true'} onChange={e => setzen(item.id, e.target.checked ? 'true' : '')}
                    className="haken" />
                )}
                {item.typ === 'TEXT' && <ChecklistenEingabe type="text" wert={item.wert || ''} onSpeichern={v => setzen(item.id, v)}
                  className="min-h-[48px] flex-1 rounded border border-dark-border bg-dark-bg px-3 py-2 text-base text-dark-text" placeholder="Freitext..." />}
                {item.typ === 'NUMBER' && <ChecklistenEingabe type="number" wert={item.wert && Number.isFinite(Number(item.wert)) ? item.wert : ''} onSpeichern={v => setzen(item.id, v)}
                  className="min-h-[48px] w-32 rounded border border-dark-border bg-dark-bg px-3 py-2 text-base text-dark-text" placeholder="Zahl" />}
                <div className="min-w-0 flex-1">
                  <p className={`text-base font-medium ${item.pflicht ? 'text-dark-text' : 'text-dark-text-muted'}`}>
                    {item.text}{item.pflicht && <span className="checklist-pflicht">Pflicht</span>}
                  </p>
                  {item.hinweis && <p className="mt-0.5 text-sm text-dark-text-muted">{item.hinweis}</p>}
                </div>
              </div>
            </ChecklistZeile>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-dark-border bg-dark-bg-secondary p-4">
          <Button variant="secondary" className="min-h-[48px]" onClick={() => onClose(false)}>Später</Button>
          <Button variant="secondary" className="min-h-[48px]" onClick={async () => { if (await frageAlleOk(instanz?.items.filter(i => i.typ === 'CHECK' && i.pflicht && !i.wert).length)) alleSetzen() }} disabled={!instanz}>Alle Pflichtpunkte ok</Button>
          <Button className="min-h-[48px]" onClick={abschliessen} disabled={!instanz || offen > 0 || speichert}>
            {offen > 0 ? `Noch ${offen} offen` : 'Abschließen'}
          </Button>
        </div>
      </div>
    </div>
  )
}
